import {
  DEFAULT_INSPECTION_DATA_OPTIONS,
  DEFAULT_INSPECTION_LINK_TO_HUSTRO,
  DEFAULT_INSPECTION_SECTION_MASTERS,
  DEFAULT_INSPECTION_TEMPLATE_DATA,
  ImageDisplayStyle,
  ReportType,
  SectionConfig,
} from 'shared/domain/report/types/model/common';
import { isImageDisplayStyle } from 'shared/domain/report/types/model/reportConfigGuards';
import { isPlainObject } from 'shared/utils/object';
import { normalizeBoolean } from 'shared/utils/value';
import { LocalStorage } from './LocalStorage';
import { LocalStorageKey, LocalStorageKeyFactory } from './keys';
import { getSavedReportConfig } from './pdfReportPreferencesStorage';
import {
  tryMigrateRawInspectionContentConfig,
} from './inspectionReportContentMigration';
import type { SavedInspectionReportContentConfig } from './inspectionReportContentStorageTypes';

export type { SavedInspectionReportFileTextMetadata } from './inspectionReportContentStorageTypes';
export type { SavedInspectionReportContentConfig } from './inspectionReportContentStorageTypes';

// Bump when the persisted shape breaks; storage key suffix follows this version.
export const INSPECTION_REPORT_CONTENT_CONFIG_VERSION = 2;

const V1_STORAGE_VERSION = 1;

export function getInspectionReportContentConfigStorageKey(): LocalStorageKey {
  return LocalStorageKeyFactory.createInspectionReportContentConfigKey(
    INSPECTION_REPORT_CONTENT_CONFIG_VERSION
  );
}

function getInspectionReportContentConfigV1StorageKey(): LocalStorageKey {
  return LocalStorageKeyFactory.createInspectionReportContentConfigKey(
    V1_STORAGE_VERSION
  );
}

const DEFAULT_FILE_TEXT_METADATA: SavedInspectionReportContentConfig['fileTextMetadata'] =
  {
    showFileNames: false,
    showFileDescriptions: false,
    showActiveLinks: false,
  };

function normalizeSectionConfig(
  defaults: SectionConfig,
  partial: unknown
): SectionConfig {
  if (!isPlainObject(partial)) {
    return { ...defaults };
  }

  const saved = partial as Partial<SectionConfig>;
  return {
    enabled: normalizeBoolean(saved.enabled, defaults.enabled),
    displayStyle:
      saved.displayStyle !== undefined &&
      isImageDisplayStyle(saved.displayStyle)
        ? saved.displayStyle
        : defaults.displayStyle,
  };
}

export function buildDefaultInspectionReportContentConfig(): SavedInspectionReportContentConfig {
  return {
    ...DEFAULT_INSPECTION_SECTION_MASTERS,
    inspectionInfo: true,
    summary: true,
    signatures: true,
    generalStandard: DEFAULT_INSPECTION_TEMPLATE_DATA.generalStandard,
    generalStandardAttachments: {
      ...DEFAULT_INSPECTION_TEMPLATE_DATA.generalStandardAttachments,
    },
    sectionDescriptions: DEFAULT_INSPECTION_TEMPLATE_DATA.sectionDescriptions,
    sectionAttachments: {
      ...DEFAULT_INSPECTION_TEMPLATE_DATA.sectionAttachments,
    },
    controlledElementStandard:
      DEFAULT_INSPECTION_TEMPLATE_DATA.controlledElementStandard,
    controlledElementStandardAttachments: {
      enabled: true,
      displayStyle: ImageDisplayStyle.miniatures,
    },
    controlledElementAttachments: {
      ...DEFAULT_INSPECTION_DATA_OPTIONS.controlledElementAttachments,
    },
    showNotApplicable: DEFAULT_INSPECTION_DATA_OPTIONS.showNotApplicable,
    showUnchecked: DEFAULT_INSPECTION_DATA_OPTIONS.showUnchecked,
    linkToHustro: DEFAULT_INSPECTION_LINK_TO_HUSTRO,
    fileTextMetadata: { ...DEFAULT_FILE_TEXT_METADATA },
  };
}

function normalizeFileTextMetadata(
  raw: unknown,
  defaults: SavedInspectionReportContentConfig['fileTextMetadata'] = DEFAULT_FILE_TEXT_METADATA
): SavedInspectionReportContentConfig['fileTextMetadata'] {
  if (!isPlainObject(raw)) {
    return { ...defaults };
  }

  const saved = raw as Partial<SavedInspectionReportContentConfig['fileTextMetadata']>;
  const showActiveLinks = normalizeBoolean(
    saved.showActiveLinks,
    defaults.showActiveLinks
  );
  const showFileNames = normalizeBoolean(
    saved.showFileNames,
    defaults.showFileNames
  );

  return {
    showFileNames: showActiveLinks && !showFileNames ? true : showFileNames,
    showFileDescriptions: normalizeBoolean(
      saved.showFileDescriptions,
      defaults.showFileDescriptions
    ),
    showActiveLinks,
  };
}

export function normalizeInspectionReportContentConfig(
  raw: unknown
): SavedInspectionReportContentConfig {
  const migrated = tryMigrateRawInspectionContentConfig(raw);
  if (migrated) {
    return normalizeInspectionReportContentConfig(migrated);
  }

  const defaults = buildDefaultInspectionReportContentConfig();
  if (!isPlainObject(raw)) {
    return defaults;
  }

  const saved = raw as Partial<SavedInspectionReportContentConfig>;
  return {
    templateSectionEnabled: normalizeBoolean(
      saved.templateSectionEnabled,
      defaults.templateSectionEnabled
    ),
    checklistSectionEnabled: normalizeBoolean(
      saved.checklistSectionEnabled,
      defaults.checklistSectionEnabled
    ),
    issueDataSectionEnabled: normalizeBoolean(
      saved.issueDataSectionEnabled,
      defaults.issueDataSectionEnabled
    ),
    inspectionInfo: normalizeBoolean(
      saved.inspectionInfo,
      defaults.inspectionInfo
    ),
    summary: normalizeBoolean(saved.summary, defaults.summary),
    signatures: normalizeBoolean(saved.signatures, defaults.signatures),
    generalStandard: normalizeBoolean(
      saved.generalStandard,
      defaults.generalStandard
    ),
    generalStandardAttachments: normalizeSectionConfig(
      defaults.generalStandardAttachments,
      saved.generalStandardAttachments
    ),
    sectionDescriptions: normalizeBoolean(
      saved.sectionDescriptions,
      defaults.sectionDescriptions
    ),
    sectionAttachments: normalizeSectionConfig(
      defaults.sectionAttachments,
      saved.sectionAttachments
    ),
    controlledElementStandard: normalizeBoolean(
      saved.controlledElementStandard,
      defaults.controlledElementStandard
    ),
    controlledElementStandardAttachments: normalizeSectionConfig(
      defaults.controlledElementStandardAttachments,
      saved.controlledElementStandardAttachments
    ),
    controlledElementAttachments: normalizeSectionConfig(
      defaults.controlledElementAttachments,
      saved.controlledElementAttachments
    ),
    showNotApplicable: normalizeBoolean(
      saved.showNotApplicable,
      defaults.showNotApplicable
    ),
    showUnchecked: normalizeBoolean(
      saved.showUnchecked,
      defaults.showUnchecked
    ),
    linkToHustro: normalizeBoolean(saved.linkToHustro, defaults.linkToHustro),
    fileTextMetadata: normalizeFileTextMetadata(
      saved.fileTextMetadata,
      defaults.fileTextMetadata
    ),
  };
}

function backfillFileTextMetadataFromSectionsConfig(
  raw: unknown
): SavedInspectionReportContentConfig['fileTextMetadata'] | undefined {
  if (!isPlainObject(raw) || raw.fileTextMetadata !== undefined) {
    return undefined;
  }

  const sections = getSavedReportConfig(ReportType.inspection);
  return {
    showFileNames: sections.showFileNames,
    showFileDescriptions: sections.showFileDescriptions,
    showActiveLinks: sections.showActiveLinks,
  };
}

function readAndNormalizeStoredConfig(
  key: LocalStorageKey
): SavedInspectionReportContentConfig | null {
  const existing = LocalStorage.get<SavedInspectionReportContentConfig | null>(
    key,
    null
  );

  if (existing === null) {
    return null;
  }

  const backfill = backfillFileTextMetadataFromSectionsConfig(existing);
  const normalized = normalizeInspectionReportContentConfig(
    backfill ? { ...existing, fileTextMetadata: backfill } : existing
  );

  if (backfill) {
    LocalStorage.set(key, normalized);
  }

  return normalized;
}

/**
 * Reads inspection report content config from localStorage (v2), migrating v1 when needed.
 */
export function getSavedInspectionReportContentConfig(): SavedInspectionReportContentConfig {
  const v2Key = getInspectionReportContentConfigStorageKey();
  const v2 = readAndNormalizeStoredConfig(v2Key);
  if (v2 !== null) {
    return v2;
  }

  const v1Key = getInspectionReportContentConfigV1StorageKey();
  const v1Raw = LocalStorage.get<unknown>(v1Key, null);
  if (v1Raw !== null) {
    const migrated = normalizeInspectionReportContentConfig(v1Raw);
    LocalStorage.set(v2Key, migrated);
    return migrated;
  }

  // One-time migration when no content-config key exists (PT-6982 / PT-6990).
  const defaults = buildDefaultInspectionReportContentConfig();
  LocalStorage.set(v2Key, defaults);
  return defaults;
}

export function saveInspectionReportContentConfig(
  config: SavedInspectionReportContentConfig
): void {
  const key = getInspectionReportContentConfigStorageKey();
  LocalStorage.set(key, normalizeInspectionReportContentConfig(config));
}
